import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import STOCK_URL from "../../utils/STOCK_URL.js";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-container": {
    padding: "2rem",
  },
  "& .MuiDialog-paper": {
    backgroundColor: "#121212",
    opacity: 0.85,
    color: "#ffffff",
    width: 1100,
    height: 500,
    maxWidth: "none",
    maxHeight: "none",
    border: "1px solid #464646",
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#000000",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 1.5rem",
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  "& .MuiInputLabel-root": {
    color: "#ffffff !important",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ffffff !important",
  },
  "& .MuiOutlinedInput-root": {
    color: "#ffffff !important",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#464646 !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ffffff !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1976d2 !important",
    },
  },
  "& .MuiSvgIcon-root": {
    color: "#ffffff !important",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: "#121212 !important",
  color: "#ffffff !important",
  "&:hover": {
    backgroundColor: "#333333 !important",
  },
  "&.Mui-selected": {
    backgroundColor: "#1976d2 !important",
  },
}));

function MonthStockGraph({ open, currentMchId, setMonthStockGraphOpen }) {
  const { group_code, project_code } = useParams();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);
  useEffect(() => {
    async function fetchAvailableYears() {
      try {
        const result = await axios.get(
          STOCK_URL +
            `/api/stock/yearInfo?group_code=${group_code}&project_code=${project_code}&currentMchId=${currentMchId}`
        );
        setAvailableYears(result.data);
      } catch (e) {
        console.log(e);
      }
    }
    if (open) {
      fetchAvailableYears();
    }
  }, [open, group_code, project_code]);
  useEffect(() => {
    async function fetchMonthlyData() {
      try {
        const result = await axios.get(
          STOCK_URL +
            `/api/stock/monthGraph?group_code=${group_code}&project_code=${project_code}&currentMchId=${currentMchId}&target_year=${selectedYear}`
        );
        const formattedData = result.data.map((item) => {
          let stockRatio;
          let displayLabel;
          if (
            item.total_stock_out === 0 ||
            item.total_stock_out === null ||
            item.total_stock_out === undefined
          ) {
            stockRatio = 0;
            displayLabel = "데이터 없음";
          } else if (
            item.total_stock_quantity === 0 ||
            item.total_stock_quantity === null ||
            item.total_stock_quantity === undefined
          ) {
            stockRatio = 0;
            displayLabel = "데이터 없음";
          } else {
            stockRatio = item.total_stock_quantity / item.total_stock_out;
            if (isNaN(stockRatio) || !isFinite(stockRatio)) {
              stockRatio = 0;
              displayLabel = "데이터 없음";
            } else {
              displayLabel = stockRatio.toFixed(1) + "일";
            }
          }
          return {
            month: `${item.month}월`,
            재고일수: stockRatio,
            재고량: item.total_stock_quantity || 0,
            출고량: item.total_stock_out || 0,
            라벨: displayLabel,
          };
        });
        setChartData(formattedData);
      } catch (e) {
        console.log(e);
      }
    }
    if (open && selectedYear) {
      fetchMonthlyData();
    }
  }, [open, selectedYear, group_code, project_code, currentMchId]);
  if (!open) return null;
  const handleClose = () => setMonthStockGraphOpen(false);
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#333333",
            border: "1px solid #464646",
            borderRadius: "4px",
            color: "#ffffff",
            padding: "10px",
          }}
        >
          <p style={{ margin: 0, marginBottom: "5px" }}>{`${label}`}</p>
          <p
            style={{ margin: 0, color: "#4285f4" }}
          >{`재고량: ${data.재고량.toLocaleString()}`}</p>
          <p
            style={{ margin: 0, color: "#34a853" }}
          >{`출고량: ${data.출고량.toLocaleString()}`}</p>
          <p style={{ margin: 0, color: "#ff9800" }}>
            {`재고일수: ${data.라벨}`}
          </p>
        </div>
      );
    }
    return null;
  };
  const renderCustomLabel = (props) => {
    const { x, y, width, payload } = props;
    if (!payload || !payload.라벨) {
      return null;
    }
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#ffffff"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
      >
        {payload.라벨}
      </text>
    );
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <DialogHeader>
        <Typography
          sx={{
            margin: 0,
            fontSize: "1.5em",
            fontWeight: 700,
            color: "#ffffff",
          }}
        >
          재고 일수
        </Typography>
        <StyledFormControl size="small">
          <InputLabel>연도</InputLabel>
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            label="연도"
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#121212",
                  border: "1px solid #464646",
                },
              },
            }}
          >
            {availableYears.map((year) => (
              <StyledMenuItem key={year.year} value={year.year}>
                {year.year}
              </StyledMenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </DialogHeader>
      <Divider />
      <DialogContent
        sx={{ padding: "1rem", backgroundColor: "#121212", height: "100%" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 50,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#464646" />
            <XAxis dataKey="month" tick={{ fill: "#ffffff" }} />
            <YAxis
              tick={{ fill: "#ffffff" }}
              label={{
                value: "재고일수",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#ffffff" },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="재고일수" fill="#ff9800">
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </DialogContent>
    </StyledDialog>
  );
}

export default MonthStockGraph;
